/**
 * One public write, with the four states a form has to render.
 *
 * Canon `docs/TESTING.md` § UI component/integration names forms and validation as
 * separately failable behaviour, so the states are modelled explicitly rather than inferred
 * from whether a result happens to be set.
 *
 * The division of labour is fixed and deliberate:
 *
 *   - `prepare` runs the **local** checks and builds the exact request body. It answers
 *     quickly and costs nothing, but it is never the authority;
 *   - `send` performs the request. Core validates every field again, and when it disagrees
 *     its `fields` array replaces the local errors wholesale — the server's answer wins,
 *     including on fields the browser was happy with.
 *
 * A submission is guarded against a double send: a second submit while one is in flight is
 * ignored, because a founder pressing the button twice must not create two deals.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "../api/client";
import { fieldErrorsFromApi } from "../lib/form";
import type { FieldErrors } from "../lib/form";
import type { FormEvent } from "react";

export type PreparedBody<TBody> =
  | { ok: true; body: TBody }
  | { ok: false; errors: FieldErrors };

export type SubmitStatus = "idle" | "submitting" | "error" | "success";

export interface SubmitAction<TResult> {
  status: SubmitStatus;
  /** Set only when the request itself was refused; `null` for purely local validation. */
  error: ApiError | null;
  result: TResult | null;
  fieldErrors: FieldErrors;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  /** Returns to `idle` and clears every error — used when a form is reopened. */
  reset: () => void;
}

export function useSubmitAction<TBody, TResult>(options: {
  prepare: () => PreparedBody<TBody>;
  send: (body: TBody, signal?: AbortSignal) => Promise<TResult>;
  /** Message attached to a server field issue that arrived without wording of its own. */
  fallbackFieldMessage?: string | undefined;
}): SubmitAction<TResult> {
  const [status, setStatus] = useState<SubmitStatus>("idle");
  const [error, setError] = useState<ApiError | null>(null);
  const [result, setResult] = useState<TResult | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  // `prepare` and `send` close over the current form values, so they are new functions on
  // every render. Keeping them in a ref means the submit handler is stable and always calls
  // the latest version, instead of a stale closure captured when the form first rendered.
  // The ref is updated in an effect rather than during render: effects run before any event
  // handler can fire, so the handler always sees the values from the render the visitor is
  // actually looking at.
  const latest = useRef(options);
  useEffect(() => {
    latest.current = options;
  });

  const controller = useRef<AbortController | null>(null);
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
      controller.current?.abort();
    };
  }, []);

  const reset = useCallback(() => {
    controller.current?.abort();
    controller.current = null;
    setStatus("idle");
    setError(null);
    setResult(null);
    setFieldErrors({});
  }, []);

  const onSubmit = useCallback((event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (controller.current !== null) return;

    const { prepare, send, fallbackFieldMessage } = latest.current;
    const prepared = prepare();
    if (!prepared.ok) {
      setStatus("error");
      setError(null);
      setFieldErrors(prepared.errors);
      return;
    }

    setStatus("submitting");
    setError(null);
    setFieldErrors({});

    const active = new AbortController();
    controller.current = active;

    void send(prepared.body, active.signal)
      .then((value) => {
        if (!live.current) return;
        setResult(value);
        setStatus("success");
      })
      .catch((caught: unknown) => {
        if (!live.current) return;
        const apiError =
          caught instanceof ApiError
            ? caught
            : new ApiError("UNEXPECTED", "Dealbid couldn't complete this right now.");
        setError(apiError);
        setFieldErrors(
          fieldErrorsFromApi(apiError, fallbackFieldMessage ?? "Check this field."),
        );
        setStatus("error");
      })
      .finally(() => {
        if (controller.current === active) controller.current = null;
      });
  }, []);

  return { status, error, result, fieldErrors, onSubmit, reset };
}
